import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { TEXT_COLORS, BORDER_COLORS, BG_COLORS } from '../../utils/colors';
import { BUTTON_SIZES } from '../../utils/responsive';

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Кнопки «Очистить» / «Применить»: закреплены внизу, не уезжают со скроллом полей */
  actions?: React.ReactNode;
  /** Поля фильтра */
  children: React.ReactNode;
}

/**
 * Нижняя шторка фильтров — ОДНА на всю админку.
 *
 * Три части: закреплённая шапка (заголовок + крестик), прокручиваемое тело и закреплённый низ
 * с кнопками. Пока шторка была просто `p: 2` без прокрутки, длинные фильтры (промокоды, поддержка)
 * не влезали в `maxHeight: 80vh` — кнопки «Очистить»/«Применить» оказывались за нижней границей
 * и до них было не добраться.
 *
 * Кнопкам в `actions` навязываются высота из `BUTTON_SIZES` и равная ширина: `GlassButton` с его
 * зашитым `padding: 6px 24px` растекался на всю ширину шторки и выглядел вдвое крупнее всего вокруг.
 */
export const FilterDrawer: React.FC<FilterDrawerProps> = ({ open, onClose, actions, children }) => {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: BG_COLORS.CARD,
          backdropFilter: 'blur(10px)',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          maxHeight: '85dvh',
          border: `1px solid ${BORDER_COLORS.LIGHT}`,
          borderBottom: 'none',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Шапка — не скроллится */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${BORDER_COLORS.DEFAULT}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: TEXT_COLORS.PRIMARY }}>
          {t('common:filters')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: TEXT_COLORS.MUTED }} />
        </IconButton>
      </Box>

      {/* Поля — единственная прокручиваемая часть */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          pt: 2,
          pb: actions ? 1 : 2,
          // Вертикальный ритм задаётся ЗДЕСЬ, а не на страницах. Страницы с большими
          // фильтрами оборачивали каждое поле в `<Box sx={{ mb: 2 }}>`, а страницы с двумя
          // полями отдавали их как есть — и там поля слипались, а подпись второго ложилась
          // на рамку первого.
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '& .MuiFormControl-root': { width: '100%', minWidth: 0 },
        }}
      >
        {children}
      </Box>

      {/* Кнопки — не скроллятся */}
      {actions && (
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${BORDER_COLORS.DEFAULT}`,
            '& .MuiButton-root': {
              flex: 1,
              minWidth: 0,
              height: BUTTON_SIZES.height,
              minHeight: 0,
              py: 0,
              px: 1.5,
              fontSize: BUTTON_SIZES.fontSize,
              whiteSpace: 'nowrap',
            },
          }}
        >
          {actions}
        </Box>
      )}
    </Drawer>
  );
};

export default FilterDrawer;
